import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  GridItem,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/carousel_arrow_left.svg';
import { ReactComponent as CheckSquareIcon } from 'assets/icons/check_square.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg';
import { ReactComponent as FolderMoveIcon } from 'assets/icons/folderMove.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plusSquareOutline.svg';
import { useTranslation } from 'react-i18next';

import { FolderProps } from './Folders.helper';

interface FooterProps {
  selectedImages: string[];
  selectedFolder: FolderProps;
  userFolders: FolderProps[];
  addToPage: () => void;
  onDelete: () => void;
  selectImages: (imageId: string[]) => void;
  onMoveToFolder: (folder: FolderProps) => void;
}
const BUTTON_SIZE = 'sm';
const defaultStyle = {
  defaultFontWeight: 'var(--vg-fontWeights-medium)',
  iconSize: {
    width: '20px',
    height: '20px',
  },
};

/**
 * Component with folder actions
 * @param FooterProps
 * @returns
 */
export const Footer = ({
  selectedImages,
  selectedFolder,
  userFolders,
  addToPage,
  onDelete,
  onMoveToFolder,
  selectImages,
}: FooterProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.imagesMenu.folders' });
  const isDisabled = selectedImages.length === 0;
  const defaultFolder: FolderProps = {
    value: '',
    label: t('allUploads'),
  };

  /**
   * Handler when a user clicks on move to folder
   * @param folder
   */
  const handleClickOnMoveToFolder = (folder: FolderProps) => {
    onMoveToFolder(folder);
  };

  const deselectAllImages = () => {
    selectImages([]);
  };

  return (
    <GridItem area='footer' position='absolute' right={0} bottom={0} width='75%'>
      <Divider />
      <Flex px={6} py={4} borderRadius='base' justifyContent='space-between' background='white' position='relative'>
        <Box>
          {selectedImages.length > 0 && (
            <Flex gap='2' alignItems='center'>
              <Button
                aria-label=''
                variant='ghost'
                size='sm'
                leftIcon={<CheckSquareIcon color='white' />}
                onClick={deselectAllImages}
                fontWeight={defaultStyle.defaultFontWeight}
              >
                <Text fontSize='sm'>
                  {selectedImages.length} {t('imagesSelected')}
                </Text>
              </Button>
            </Flex>
          )}
        </Box>
        <ButtonGroup>
          <Menu strategy='fixed'>
            <MenuButton
              as={Button}
              leftIcon={<FolderMoveIcon {...defaultStyle.iconSize} />}
              variant='outline'
              size={BUTTON_SIZE}
              isDisabled={isDisabled}
              fontWeight={defaultStyle.defaultFontWeight}
            >
              {t('move')}
            </MenuButton>
            <MenuList>
              <MenuGroup>
                <Flex gap='2' mx='2' alignItems='baseline'>
                  <ArrowLeftIcon />{' '}
                  <Text fontSize='sm' fontWeight={'semibold'}>
                    {t('moveTo')}...
                  </Text>
                </Flex>
                <MenuDivider />
                <Box maxH='200px' overflow='auto'>
                  <MenuItem
                    onClick={handleClickOnMoveToFolder.bind(null, defaultFolder)}
                    isDisabled={selectedFolder.value === ''}
                  >
                    <Text fontSize={'sm'} fontWeight={'500'} flexGrow='1' textAlign='left'>
                      {defaultFolder.label}
                    </Text>
                  </MenuItem>
                  {userFolders.map((folder) => (
                    <MenuItem
                      icon={<FolderIcon />}
                      key={folder.value}
                      onClick={handleClickOnMoveToFolder.bind(null, folder)}
                      isDisabled={folder.value === selectedFolder.value}
                    >
                      <Text fontSize={'sm'} fontWeight={'500'} flexGrow='1' textAlign='left'>
                        {folder.label}
                      </Text>
                    </MenuItem>
                  ))}
                </Box>
              </MenuGroup>
            </MenuList>
          </Menu>
          <Button
            variant={'outline'}
            onClick={onDelete}
            leftIcon={<DeleteIcon {...defaultStyle.iconSize} />}
            size={BUTTON_SIZE}
            isDisabled={isDisabled}
            fontWeight={defaultStyle.defaultFontWeight}
          >
            {t('delete')}
          </Button>
          <Button
            leftIcon={<PlusIcon fill={isDisabled ? 'black' : 'white'} {...defaultStyle.iconSize} />}
            variant={isDisabled ? 'outline' : 'action'}
            size={BUTTON_SIZE}
            isDisabled={isDisabled}
            onClick={addToPage}
            fontWeight={defaultStyle.defaultFontWeight}
          >
            {t('addToPage')}
          </Button>
        </ButtonGroup>
      </Flex>
    </GridItem>
  );
};
